// @ts-strict-ignore
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { RootState } from "../../common/state/store";
import {
  getEditedProjectGroup,
  getNewProjectGroup,
  getSelectedProjectGroup,
  setEditedMenuMedia,
  setNewProjectGroup,
  updateProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { DynamicMedia } from "../../features/graph/components/DynamicField";
import {
  mediaSelector,
  setMediaSearch,
  setOriginalMediaGallery,
} from "../../features/gallery/state/gallerySlice";
import { useEffect, useState } from "react";
import type { IMedia } from "../../model/unityObject";
import { closeStandardModal, openStandardModal } from "../../common/state/slice/modal/modalSlice";
import ModalGallery from "../../features/graph/components/ModalGallery";
import { FooterButtons } from "../../common/components/FooterButtons";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { setAlert } from "src/features/alert/state/alertsSlice";

interface ICreateProjectGroupMenuMedia {
  incrementStep?: () => void;
  decrementStep?: () => void;
  editMode?: boolean;
}

const MAX_MEDIA_SIZE_IN_BYTE = 30 * 1024 * 1024; // => 30 Mo

const CreateProjectGroupMenuMedia = ({
  incrementStep,
  decrementStep,
  editMode = false,
}: ICreateProjectGroupMenuMedia) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { register, handleSubmit, setValue, control } = useForm<any>();

  const mediasList = useAppSelector(mediaSelector);

  const [currentMenuMediaId, setCurrentMenuMediaId] = useState<number>(5346);

  const { newProjectGroup, selectedProjectGroup, editedProjectGroup } = useAppSelector(
    (state: RootState) => {
      return {
        newProjectGroup: getNewProjectGroup(state),
        selectedProjectGroup: getSelectedProjectGroup(state),
        editedProjectGroup: getEditedProjectGroup(state),
      };
    },
  );

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  const relevantMedia = mediasList.find((m: IMedia) => Number(m.id) === Number(currentMenuMediaId));

  // Dance around the media preview issues that arose from repurposing the "EditNodeForm" component
  const getEnhancedBgMusicField = () => {
    const enhancedField = {
      type: "media",
      mediaType: "image",
      fieldName: "source_app_main_menu_media",
      currentValue: currentMenuMediaId,
      relevantSourceMedia: {
        ...relevantMedia,
        s3_url: relevantMedia?.thumbnail_s3_url || relevantMedia?.s3_url,
      },
    };
    if (!currentMenuMediaId) {
      enhancedField.currentValue = -1;
      enhancedField.relevantSourceMedia = mediasList.find(
        (m: IMedia) => Number(m.id) === Number(currentMenuMediaId),
      );
    }
    return enhancedField;
  };

  const openGallery = () => {
    dispatch(
      setMediaSearch({
        mediaType: "image",
        media360tag: "media360",
      }),
    );
    dispatch(setOriginalMediaGallery(currentMenuMediaId));
    dispatch(openStandardModal("modal-gallery"));
  };

  const onSubmit = () => {
    openGallery();
  };

  // Callback from ModalGallery selection
  const onMediaChange = (selectedMedia: IMedia) => {
    dispatch(closeStandardModal("modal-gallery"));

    setCurrentMenuMediaId(Number(selectedMedia.id));
  };

  const handleSave = () => {
    if (editMode) {
      void dispatch(
        updateProjectGroup({
          id: selectedProjectGroup.id,
          payload: {
            source_app_main_menu_media: enhancedField.relevantSourceMedia,
          },
          componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(setAlert({ msg: t("alerts.projectGroupUpdateSuccess"), type: "success" }));
          dispatch(setEditedMenuMedia(enhancedField.relevantSourceMedia as IMedia));
          navigate(`/courses/${selectedProjectGroup.id}/list`);
        });
    }
  };

  const handleCancel = () => {
    navigate(`/courses/${selectedProjectGroup.id}/list`);
  };

  useEffect(() => {
    if (editMode) {
      const tbnId = Number(editedProjectGroup?.source_app_main_menu_media?.id) || 5346;
      setValue("menuMediaId", tbnId);
      setCurrentMenuMediaId(tbnId);
    } else {
      const tbnId = newProjectGroup.menuMediaId;
      setValue("menuMediaId", tbnId);
      setCurrentMenuMediaId(tbnId);
    }
  }, [editedProjectGroup.source_app_main_menu_media, newProjectGroup.menuMediaId, setValue]);

  const enhancedField = getEnhancedBgMusicField();

  return (
    <>
      <h1 className="text-2xl my-5 px-6 font-medium">{t("general.selectMenuMedia")}</h1>
      <ModalGallery
        nodeId={"new-project-group-menu-sphere-app"}
        onMediaChange={onMediaChange}
        allowedMediaTypeOverride="image"
      />

      <form className="flex flex-col grow w-full justify-between" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-10 w-full my-5 px-6">
          <div className="flex flex-col items-center justify-center bg-green-200 p-6 w-full max-w-max">
            <Controller
              control={control}
              name={"source_app_main_menu_media"}
              render={({ field }) => (
                <DynamicMedia
                  field={field as any}
                  fieldType={enhancedField.type}
                  register={register}
                  handleButtonClick={() => {
                    openGallery();
                  }}
                  currentMedia={enhancedField.currentValue as number}
                  media={enhancedField.relevantSourceMedia as IMedia}
                />
              )}
            />
          </div>
          {enhancedField?.relevantSourceMedia?.size_in_bytes > MAX_MEDIA_SIZE_IN_BYTE && (
            <p className="text-red-500">{t("pages.createProjectGroup.menuMediaTooBig")}</p>
          )}
        </div>

        {editMode ? (
          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={handleCancel}
            nextText={t("general.save")}
            nextCb={handleSave}
          />
        ) : (
          <FooterButtons
            cancelText={t("general.cancel")}
            cancelCb={() =>
              navigate(`/courses${editMode ? `/${selectedProjectGroup.id}/list` : ""}`)
            }
            backText={t("general.back")}
            backCb={decrementStep}
            nextText={t("general.next")}
            nextCb={() => {
              if (editMode) {
                dispatch(setEditedMenuMedia(enhancedField.relevantSourceMedia as IMedia));
              } else {
                dispatch(
                  setNewProjectGroup({ ...newProjectGroup, menuMediaId: currentMenuMediaId }),
                );
              }

              if (incrementStep) {
                incrementStep();
              }
            }}
            nextDisabled={
              !enhancedField.relevantSourceMedia ||
              enhancedField?.relevantSourceMedia?.size_in_bytes > MAX_MEDIA_SIZE_IN_BYTE
            }
          />
        )}
      </form>
    </>
  );
};

export default CreateProjectGroupMenuMedia;
